import { useState, useCallback, useMemo } from 'react';
import { AmountInput } from './AmountInput';
import { useRates } from '../../hooks/useRates';
import { debounce } from '../../lib/utils';
import { DEBOUNCE_DELAY } from '../../lib/constants';

interface ConversionGridProps {
  currencies: string[];
}

export function ConversionGrid({ currencies }: ConversionGridProps) {
  const { convertAmount } = useRates();
  const [amounts, setAmounts] = useState<Record<string, string>>({});
  const [baseCurrency, setBaseCurrency] = useState<string | null>(null);
  const [focusedCurrency, setFocusedCurrency] = useState<string | null>(null);

  // Debounced update function
  const debouncedUpdate = useMemo(
    () => debounce((newBaseCurrency: string, baseAmount: string) => {
      const numericAmount = parseFloat(baseAmount) || 0;
      const newAmounts: Record<string, string> = { [newBaseCurrency]: baseAmount };

      // Calculate all other currency amounts
      currencies.forEach(currency => {
        if (currency !== newBaseCurrency) {
          const convertedAmount = convertAmount(numericAmount, newBaseCurrency, currency);
          newAmounts[currency] = convertedAmount > 0 ? convertedAmount.toString() : '';
        }
      });

      setAmounts(newAmounts);
    }, DEBOUNCE_DELAY),
    [currencies, convertAmount]
  );

  const handleAmountChange = useCallback((currency: string, value: string) => {
    // Update the current input immediately
    setAmounts(prev => ({ ...prev, [currency]: value }));
    
    // Set this currency as the base
    setBaseCurrency(currency);
    
    // Debounce the conversion calculation
    if (value && parseFloat(value) > 0) {
      debouncedUpdate(currency, value);
    } else {
      // Clear all amounts if input is empty
      const emptyAmounts: Record<string, string> = {};
      currencies.forEach(curr => {
        emptyAmounts[curr] = curr === currency ? value : '';
      });
      setAmounts(emptyAmounts);
    }
  }, [currencies, debouncedUpdate]);

  const handleFocus = useCallback((currency: string) => {
    setFocusedCurrency(currency);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedCurrency(null);
  }, []);

  if (currencies.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 dark:text-gray-400">
          Add some currencies to start converting
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1">
        {currencies.map((currency) => (
          <AmountInput
            key={currency}
            currency={currency}
            value={amounts[currency] || ''}
            onChange={handleAmountChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            focused={focusedCurrency === currency}
          />
        ))}
      </div>
    </div>
  );
}